/**
 * 採購單列表（P1.5-B；採購頁同訂單頁共用）
 *
 * ⚠️ 邊個掣顯示唔顯示：一律由 `@coeldery/purchase-order` 嘅 `nextStatuses()` 決定
 *    （後端 `/api/purchase-orders/:id` 回 `next`）—— 前端唔准自己寫 `if (status === …)`。
 *    呢個元件收 `next` 唔收 status。顏色／文字一律 i18n ＋ ui-kit tokens。
 */
import { useTranslation } from 'react-i18next';
import { badge, btnGhost, btnPrimary, card, hint as hintStyle } from '@coeldery/ui-kit';
import { poStatusLabel, poStatusTone } from '../lib/po-status';
import type { PurchaseOrder, PurchaseStatus } from '../lib/api';

export interface PoListProps {
  orders: readonly PurchaseOrder[];
  /** 每張單可以做嘅下一步（由後端出；`undefined` ＝ 未載入 → 唔顯示掣） */
  nextById?: Record<string, readonly PurchaseStatus[]>;
  busy?: boolean;
  emptyText: string;
  title: string;
  /** 撳咗下一步（action 由 `next` 對照：`sent`→`send`、`closed`→`close`、`cancelled`→`cancel`） */
  onAction: (po: PurchaseOrder, action: 'send' | 'close' | 'cancel' | 'receive') => void;
  /** 撳咗「核對／確認」入去改數量＋ETA（只有供應商睇到 `ack` 嘅單需要） */
  onOpen?: (po: PurchaseOrder) => void;
}

/** 狀態 → 動作（唯一對照；`ack`／`ship` 由詳情頁做，唔喺列表撳） */
const ACTION_OF: Partial<Record<PurchaseStatus, 'send' | 'close' | 'cancel' | 'receive'>> = {
  sent: 'send',
  closed: 'close',
  cancelled: 'cancel',
  received: 'receive',
};

export default function PoList({
  orders,
  nextById = {},
  busy,
  emptyText,
  title,
  onAction,
  onOpen,
}: PoListProps) {
  const { t } = useTranslation();

  return (
    <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>{title}</h3>
      {orders.length === 0 ? (
        <p style={hintStyle}>{emptyText}</p>
      ) : (
        orders.map((po) => {
          const next = nextById[po.id] ?? [];
          return (
            <div
              key={po.id}
              style={{
                padding: '10px 0',
                borderTop: '1px solid var(--color-divider)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <strong style={{ minWidth: '140px' }}>{po.poNo}</strong>
              <span style={hintStyle}>
                {po.supplierCode} {po.supplierName}
              </span>
              <span style={badge(poStatusTone(po.status))}>{t(poStatusLabel(po.status))}</span>
              {po.expectedAt && (
                <span style={hintStyle}>
                  {t('po.eta')} {po.expectedAt}
                </span>
              )}
              {po.ackNote && <span style={hintStyle}>「{po.ackNote}」</span>}

              {onOpen && next.includes('ack') && (
                <button type="button" style={btnPrimary} onClick={() => onOpen(po)} disabled={busy}>
                  {t('po.act.ack')}
                </button>
              )}
              {onOpen && next.includes('shipped') && (
                <button type="button" style={btnPrimary} onClick={() => onOpen(po)} disabled={busy}>
                  {t('po.act.ship')}
                </button>
              )}
              {next
                .map((s) => ACTION_OF[s])
                .filter((a): a is 'send' | 'close' | 'cancel' | 'receive' => Boolean(a))
                .map((action) => (
                  <button
                    key={action}
                    type="button"
                    style={action === 'cancel' ? btnGhost : btnPrimary}
                    onClick={() => onAction(po, action)}
                    disabled={busy}
                  >
                    {t(`po.act.${action}`)}
                  </button>
                ))}
            </div>
          );
        })
      )}
    </section>
  );
}
